import type {
    AreaFill,
    ImageEdit,
    Layer,
    NorthState,
    PenPreset,
    Tool,
    View
} from './types';

export const DEFAULT_NORTH: NorthState = {
    show: false,
    angle: 0,
    x: 88,
    y: 12,
    scale: 1
};

/**
 * How big the compass lands when FIRST PLACED, as a factor on the sizes below.
 * A placement scale fits the compass and nothing else: it is one drawing that
 * scales as a whole, where a nail's line weight and label must not.
 */
export const NORTH_PLACE_SCALE = 0.6;

/**
 * How far the crop's straighten slider reaches, in degrees each way. 45° is the
 * whole range: past it the next 90° step (`imgEdit.rotate`) describes the same
 * rectangle, and the crop folds it back anyway — see `foldCropAngle`.
 */
export const MAX_STRAIGHTEN = 45;

// The selection id of the single north compass. Marks carry a UUID, so this
// sentinel cannot collide with one.
export const NORTH_ID = 'north';

export const ALL_LAYERS: Layer[] = ['drawing', 'symbols', 'leaders', 'text'];

export const TOOL_LAYER: Partial<Record<Tool, Layer>> = {
    pen: 'drawing',
    eraser: 'drawing',
    nail: 'symbols',
    /**
     * A Schicht rides on the symbols layer rather than a fifth of its own: the
     * layer toggles are saved per scene, and a new one would read as "off" on
     * every sketch there is.
     */
    stratum: 'symbols',
    leader: 'leaders',
    text: 'text'
};

/**
 * A dot is a hair's length of line rather than a zero: a round cap draws it as a
 * full round dot, where a nought is where renderers disagree. Dash patterns are
 * MULTIPLES OF THE STROKE WIDTH (Stroke.dash); exported because a TAPPED stroke
 * is the same problem outside any dash (StrokeMark).
 */
export const DOT = 0.01;
/** Profilkasten: dash, dot, dash, dot — the surveyor's chain-dotted line. */
export const DASH_DASHDOT = [4, 2, DOT, 2];
/** Schichtgrenze: an evenly dashed line. */
export const DASH_DASHED = [4, 2.5];

export const PEN_GROUPS = [
    'sketch.penGroup.general',
    'sketch.penGroup.inclusions',
    'sketch.penGroup.finds'
] as const;

/**
 * The Stiftart list: what a line is DRAWN AS - a colour and a line pattern
 * together. The find-type colours are the Amt's ("How To - Colours for finds in
 * drawings"), approximating its Faber-Castell Polychromos pencils.
 *
 * findPenPreset resolves a drawn line back to its preset by COLOUR AND PATTERN,
 * so no two presets may share BOTH.
 */
/**
 * The house ink every general pen draws in: a dark slate. It replaced the AST
 * logo's brown `#5a4c3d`; that one stays a FORMER colour, so sketches drawn before still resolve to their pens and are
 * re-drawn in this one (see currentPenColor). Declared here, ahead of
 * PEN_PRESETS: read further down it would be a temporal dead zone at load.
 */
const HOUSE_INK = '#2e2e38';
const HOUSE_INK_FORMER = ['#5a4c3d'];

export const PEN_PRESETS: PenPreset[] = [
    // --- Allgemein ------------------------------------------------------
    // NEUTRAL BY DESIGN: no `code`, so text, leaders and arrows drawn in the
    // house ink never add a find type to the legend.
    {
        id: 'outline',
        color: HOUSE_INK,
        formerColors: HOUSE_INK_FORMER,
        labelId: 'sketch.pen.outline',
        groupId: 'sketch.penGroup.general'
    },
    // The same pen as the Befund outline, in the convention the sheet reads them
    // by: chain-dotted for a profile box, dashed for a layer boundary.
    {
        id: 'profileBox',
        color: HOUSE_INK,
        formerColors: HOUSE_INK_FORMER,
        dash: DASH_DASHDOT,
        labelId: 'sketch.pen.profileBox',
        groupId: 'sketch.penGroup.general'
    },
    {
        id: 'layerBoundary',
        color: HOUSE_INK,
        formerColors: HOUSE_INK_FORMER,
        dash: DASH_DASHED,
        labelId: 'sketch.pen.layerBoundary',
        groupId: 'sketch.penGroup.general'
    },
    // Stoerung - Ultramarin 120. The one pen that draws an AREA: its outline
    // closes and what it encloses is hatched (see areaFill.ts), because a Stoerung
    // is a region of the surface and not a line across it. dE 34.6 from the
    // Silex blue, dE 20.5 from the iron violet, its nearest neighbour.
    {
        id: 'STOE',
        color: '#2b3a8f',
        fill: 'hatch',
        labelId: 'sketch.find.STOE',
        code: 'Stö',
        groupId: 'sketch.penGroup.general'
    },
    /**
     * Humus - the house ink again, told apart by its stipple, as a section
     * drawing does on paper. Which is why the fill is part of a pen's identity
     * rather than a property of how it looks (see findPenPreset).
     */
    {
        id: 'HUM',
        color: HOUSE_INK,
        formerColors: HOUSE_INK_FORMER,
        fill: 'stipple',
        labelId: 'sketch.pen.humus',
        groupId: 'sketch.penGroup.general'
    },
    {
        id: 'white',
        color: '#ffffff',
        labelId: 'sketch.pen.white',
        groupId: 'sketch.penGroup.general'
    },
    // --- Beimengungen ---------------------------------------------------
    // What sits IN the soil as material rather than being lifted as a find.
    // Holzkohle - Schwarz 199
    {
        id: 'HK',
        color: '#050505',
        labelId: 'sketch.find.HK',
        code: 'HK',
        groupId: 'sketch.penGroup.inclusions'
    },
    // Brandlehm - Kadmiumorange dunkel 115
    {
        id: 'BL',
        color: '#f47933',
        labelId: 'sketch.find.BL',
        code: 'BL',
        groupId: 'sketch.penGroup.inclusions'
    },
    // Ziegel - Terracotta 186
    {
        id: 'ZG',
        color: '#ec8e59',
        labelId: 'sketch.find.ZG',
        code: 'ZG',
        groupId: 'sketch.penGroup.inclusions'
    },
    // Stein - no pencil given by the Amt; also stands in for Moertel.
    {
        id: 'ST',
        color: '#95a5a6',
        labelId: 'sketch.find.ST',
        code: 'ST',
        groupId: 'sketch.penGroup.inclusions'
    },
    // --- Funde ----------------------------------------------------------
    // Keramik - Tiefrot 223
    {
        id: 'KE',
        color: '#e42f44',
        labelId: 'sketch.find.KE',
        code: 'KE',
        groupId: 'sketch.penGroup.finds'
    },
    // Silex - Preussischblau 246
    {
        id: 'SX',
        color: '#126495',
        labelId: 'sketch.find.SX',
        code: 'SX',
        groupId: 'sketch.penGroup.finds'
    },
    // Tierische Reste - Kadmiumgelb 107
    {
        id: 'TKN',
        color: '#ffd530',
        labelId: 'sketch.find.TKN',
        code: 'TKN',
        groupId: 'sketch.penGroup.finds'
    },
    // Menschliche Reste - the Amt sheet gives it the same yellow as the animal
    // remains, which the legend could not tell apart.
    {
        id: 'Mkn',
        color: '#c8960a',
        labelId: 'sketch.find.Mkn',
        code: 'Mkn',
        groupId: 'sketch.penGroup.finds'
    },
    // Glas - Smaragdgruen 163
    {
        id: 'GL',
        color: '#0cb074',
        labelId: 'sketch.find.GL',
        code: 'GL',
        groupId: 'sketch.penGroup.finds'
    },
    // Holz - Caput Mortuum Violet 263
    {
        id: 'HO',
        color: '#824e51',
        labelId: 'sketch.find.HO',
        code: 'HO',
        groupId: 'sketch.penGroup.finds'
    },
    // --- Metalle (Funde) ------------------------------------------------
    // Only Fe and Bz are prescribed by the Amt sheet. The other four were chosen
    // for DISTINGUISHABILITY rather than likeness, measured in Lab against every
    // other preset: silver, copper and zinc sit in bands already occupied.
    // Eisen - Purpurviolett 136 (vorgegeben)
    {
        id: 'Fe',
        color: '#7358a0',
        labelId: 'sketch.find.Fe',
        code: 'Fe',
        groupId: 'sketch.penGroup.finds'
    },
    // Bronze - Kobaltgruen 156 (vorgegeben)
    {
        id: 'Bz',
        color: '#01babd',
        labelId: 'sketch.find.Bz',
        code: 'Bz',
        groupId: 'sketch.penGroup.finds'
    },
    // Gold - gold 250; dE 32 to its nearest neighbour.
    {
        id: 'Au',
        color: '#cdc08d',
        labelId: 'sketch.find.Au',
        code: 'Au',
        groupId: 'sketch.penGroup.finds'
    },
    // Silber - silver 251. Only dE 6.7 from Stein: hard to tell apart BY EYE,
    // but the legend matches on the exact hex.
    {
        id: 'Ag',
        color: '#abadb0',
        labelId: 'sketch.find.Ag',
        code: 'Ag',
        groupId: 'sketch.penGroup.finds'
    },
    // Kupfer - copper 252, dE 19 to Stein: distinct but not comfortably so.
    {
        id: 'Cu',
        color: '#b09092',
        labelId: 'sketch.find.Cu',
        code: 'Cu',
        groupId: 'sketch.penGroup.finds'
    },
    // Zink - cobalt turquoise 153, dE 18 to Silex: likewise tight.
    {
        id: 'Zn',
        color: '#0082a2',
        labelId: 'sketch.find.Zn',
        code: 'Zn',
        groupId: 'sketch.penGroup.finds'
    }
];

// The default must stay a NON-CODED colour, or every unstyled text or arrow
// would be legended as a find type.
export const DEFAULT_PEN_ID = 'outline';
export const DEFAULT_COLOR = HOUSE_INK;

export function penPresetById(id: string): PenPreset {
    return (
        PEN_PRESETS.find((preset) => preset.id === id) ??
        PEN_PRESETS.find((preset) => preset.id === DEFAULT_PEN_ID) ??
        PEN_PRESETS[0]
    );
}

export function sameDash(firstDash?: number[], secondDash?: number[]): boolean {
    const left = firstDash ?? [];
    const right = secondDash ?? [];
    return (
        left.length === right.length &&
        left.every((value, index) => value === right[index])
    );
}

/**
 * The preset a DRAWN mark came from — colour, pattern and fill together, because
 * no two of the three identify a pen: the Befundkontur and the Humus are both
 * solid house ink and differ only in the fill. Undefined when nothing matches.
 */
export function findPenPreset(
    color: string,
    dash?: number[],
    fill?: AreaFill
): PenPreset | undefined {
    const hex = color.toLowerCase();
    return PEN_PRESETS.find(
        (preset) =>
            [preset.color, ...(preset.formerColors ?? [])].some(
                (presetColor) => presetColor.toLowerCase() === hex
            ) &&
            sameDash(preset.dash, dash) &&
            preset.fill === fill
    );
}

/**
 * The colour a stored mark is DRAWN in today: a pen's former colour becomes its
 * current one, anything else stays as it is. Applied where a scene is read, so
 * an old sketch opens, re-renders and — once saved — is stored in the new
 * colour.
 */
export function currentPenColor(color: string): string;
export function currentPenColor(color?: string): string | undefined;
export function currentPenColor(color?: string): string | undefined {
    if (!color) {
        return color;
    }
    const hex = color.toLowerCase();
    const preset = PEN_PRESETS.find((candidate) =>
        candidate.formerColors?.some((former) => former.toLowerCase() === hex)
    );
    return preset?.color ?? color;
}

/**
 * How a pen is named where it is OFFERED: "SX — Silex" where the Amt gives an
 * abbreviation, the plain name where it does not. The Befund sheet's Legende
 * prints the name alone — see SketchBlock.
 */
export function penPresetLabel(
    preset: { code?: string; labelId: string },
    translate: (id: string) => string
): string {
    const name = translate(preset.labelId);
    return preset.code ? `${preset.code} — ${name}` : name;
}

/**
 * The width a pen starts at, in REFERENCE pixels; the slider (1..24) goes either
 * way from here. THIN by default: the 300pt the Befund sheet gives a sketch
 * turns every reference pixel into about a third of a point, and at 4 the
 * drawing came out heavier than the sheet it is printed on.
 */
export const DEFAULT_STROKE_WIDTH = 2;

/**
 * Deliberately the SAME cap the photo itself is stored at (see compressImage):
 * the annotated image is what most people end up looking at, so it must not be
 * the softer of the two. The quality is a notch above the photo's own 0.90
 * because a sketch bakes its background into a SECOND JPEG generation.
 */
export const MAX_EXPORT_DIMENSION = 4032;
export const EXPORT_QUALITY = 0.92;

/**
 * The surface width a STROKE width is authored against: a pen width is stored as
 * a fraction of it and multiplied back at render (see SketchEditor, StrokeMark),
 * so one slider value draws the same relative line everywhere. Marks are sized
 * against the DIAGONAL instead — see below.
 */
export const REFERENCE_SURFACE_WIDTH = 800;

/**
 * The surface diagonal every mark size below is authored against. Mark geometry
 * is NOT on-screen pixels — the surface is ~350px wide on a phone and ~1600px on
 * a desktop — so every size here is multiplied by
 *     hypot(surface.width, surface.height) / REFERENCE_SURFACE_DIAGONAL
 * at render time (see `markPx`).
 *
 * The DIAGONAL, not the width: the Befund sheet caps a sketch at 340pt tall, so
 * a portrait drawing prints narrower and its marks would shrink twice. 1000 is
 * the diagonal of an 800x600 surface, the commonest 4:3 photo.
 */
export const REFERENCE_SURFACE_DIAGONAL = 1000;

/**
 * Annotation geometry, in REFERENCE pixels (see above), sized for the PDF rather
 * than the screen: the Befund sheet places a 4:3 sketch about 300pt wide, so an
 * annotation takes up size / REFERENCE_SURFACE_WIDTH * 300pt on paper. Scaled by
 * 1.8 to bring the lettering to roughly 9pt.
 */
export const MARK = '#d32f2f';

/**
 * The ink ALL LETTERING is set in — pointedly not the pen's colour: colour on
 * these sheets means what the ground is made of, so a note in the Silex blue
 * would claim a find that is not there. Only a drawn LINE carries a pen colour.
 */
export const LETTERING = '#000000';

/**
 * The crop frame is drawn TWICE: a dark line with a white dashed one on top.
 * It is dragged onto bright sky as readily as onto black soil, and two lines of
 * opposite brightness always leave one of them showing — the same reason a nail
 * carries a white halo.
 */
export const CROP_LINE_DARK = 'rgba(0,0,0,0.7)';
export const CROP_LINE_LIGHT = '#ffffff';

/**
 * The line weight every drawn symbol shares: a coloured line on a white halo, so
 * it holds up over a dark Planum as well as over pale sand. Read against the
 * sizes below — a 24-pixel crosshair at a 2.4 line is proportionally twice as
 * bold as a 40-pixel one.
 */
export const MARK_HALO = 4.5;
export const MARK_LINE = 2.4;

/**
 * The white OUTLINE every piece of LETTERING is set in — MARK_HALO's job for
 * text. Not a box (it covers the photo the label points at) and not a drop
 * shadow (too soft to carry black over a dark fill).
 *
 * HALF of this width shows: the fill is painted back over the inner half (see
 * `fillAfterStrokeEnabled` in marks.tsx).
 */

export const MARK_TEXT_OUTLINE = 2;

/**
 * What the outline is painted IN. The alpha belongs in the COLOUR, not in the
 * node's `opacity`, which would fade the lettering along with its outline.
 * Turned down, the photo shows through the edge; at 1 it is a hard white edge.
 */
export const MARK_TEXT_OUTLINE_COLOR = 'rgba(255,255,255,0.75)';

/**
 * The nail crosshair: small, because it marks a POINT and the label beside it
 * says what the point is. Sized here rather than scaled at placement, so line
 * weight and label do not shrink with it.
 */
export const NAIL_RADIUS = 12;
export const NAIL_ARM = 16;
/**
 * The size of EVERY piece of lettering — nail label, arrow label, Schicht number
 * and free note alike: they are all read off the same sheet at the same distance.
 */
export const LABEL_FONT = 25;
/**
 * The Schicht's box: how much room the lettering gets beside and above it, in
 * reference pixels. Its border is MARK_LINE — the box is a symbol, not a text
 * decoration. The two differ on purpose: a tighter padding above and below
 * brings a single digit close to the square the convention draws it as.
 */
export const STRATUM_PADDING_X = 6;
export const STRATUM_PADDING_Y = 2;
/**
 * The box's fill — half-transparent, so the photo or the drawing underneath
 * still reads through the number that names it.
 */
export const STRATUM_FILL = 'rgba(255,255,255,0.5)';
/** The arrow's head. Independent of MARK_LINE: a heavier head is just a blot. */
export const ARROW_POINTER = 18;
/**
 * How far a leader's label stands off the arrow's tail, along the direction the
 * shaft runs (see leaderLabelDirection) — enough to clear the line's round cap.
 */
export const LEADER_LABEL_GAP = 10;

// North compass geometry, in REFERENCE pixels (same 1.8 scale).
export const NORTH_RING = 40;
export const NORTH_TIP = 32;
export const NORTH_HANDLE = 70;
/** Type size of the compass "N". */
export const NORTH_LABEL = 24;
/** Half-width of the compass needle, at its base. */
export const NORTH_BARB = 14;

// Viewport zoom/pan (the Konva stage's own scale and position, so the drawing
// is re-drawn sharp at every zoom).
export const MAX_ZOOM = 6;
// Zooming OUT below 100% helps judge the whole sketch at a glance.
export const MIN_ZOOM = 0.5;

// Photoshop-style tool keys (V move/select, B brush/pen, E eraser, T text) —
// plus N Nagel, L Pfeil (leader) and K Kompass for our own tools.
export const TOOL_SHORTCUTS: Record<string, Tool> = {
    v: 'select',
    b: 'pen',
    e: 'eraser',
    n: 'nail',
    l: 'leader',
    t: 'text',
    s: 'stratum',
    k: 'north'
};
export const ZOOM_BUTTON_STEP = 1.5;
export const ZOOM_WHEEL_STEP = 1.15;

export const DEFAULT_VIEW: View = { zoom: 1, panX: 0, panY: 0 };

/**
 * v2: added the non-destructive background image adjustments (`img`).
 * v3: added free-text annotations (`texts`).
 * v4: the crop carries an angle (straightening), the north arrow a scale.
 * v5: a stroke may carry a dash pattern (`d`) — the Stiftart's line style.
 * v6: boxed Schicht labels (`strata`).
 */
export const SCENE_VERSION = 6;

export const DEFAULT_IMG_EDIT: ImageEdit = {
    rotate: 0,
    flipH: false,
    flipV: false,
    crop: null,
    brightness: 0,
    contrast: 0
};

// NB: no Konva import here (BG_FILTERS lives in SketchCanvas). `scene.ts` reads
// this module for the media list, and that must not drag the whole drawing
// library into the app's first load.
